"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isAiPagePathname } from "./ai-pathname";

const AI_SIDE_PANEL_OPEN_KEY = "ai-side-panel-open";

type WorkspaceSidePanelMode = "AI_CHAT" | "MEMO";

export type UserMemoContext = {
  workspaceId: string;
  featureKind:
    | "SEARCH_ANALYTICS"
    | "USER_ANALYTICS"
    | "INDEX_WORKER"
    | "DASHBOARD";
  targetKind:
    | "SEARCH_ANALYTICS_PROJECT"
    | "SEARCH_ANALYTICS_VIEW"
    | "USER_ANALYTICS_PROJECT"
    | "USER_ANALYTICS_VIEW"
    | "DASHBOARD"
    | "INDEX_WORKER_PROJECT"
    | "INDEX_WORKER";
  targetId: string;
  projectId: string;
  searchAnalyticsId: string;
  viewName: string;
};

export type MemoPanelIntent =
  | {
      state: "list";
      context: UserMemoContext;
      date?: string;
      dateStart?: string;
      dateEnd?: string;
      relatedContexts?: UserMemoContext[];
    }
  | {
      state: "detail";
      context: UserMemoContext;
      memoId: string;
      clearDateFilter?: boolean;
      returnList?: {
        context: UserMemoContext;
        relatedContexts?: UserMemoContext[];
      };
    }
  | {
      state: "create";
      context: UserMemoContext;
      initialDate?: string;
      source?: "chart" | "panel";
      relatedContexts?: UserMemoContext[];
    }
  | { state: "edit"; context: UserMemoContext; memoId: string };

export type WorkspaceSidePanelState =
  | { kind: "closed" }
  | { kind: "ai-chat" }
  | { kind: "memo"; intent: MemoPanelIntent };

type WorkspaceSidePanelContextType = {
  state: WorkspaceSidePanelState;
  mode: WorkspaceSidePanelMode;
  userMemoIntent: MemoPanelIntent | null;
  isOpen: boolean;
  isVisible: boolean;
  isAiAvailable: boolean;
  openAiChat: () => void;
  openUserMemo: (intent: MemoPanelIntent) => void;
  closeWorkspaceSidePanel: () => void;
  toggleAiChat: () => void;
};

const WorkspaceSidePanelContext =
  createContext<WorkspaceSidePanelContextType | null>(null);

function readStoredAiPanelOpenState() {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(AI_SIDE_PANEL_OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredAiPanelOpenState(isOpen: boolean) {
  try {
    localStorage.setItem(AI_SIDE_PANEL_OPEN_KEY, String(isOpen));
  } catch {
    // Storage failure must not prevent the in-memory panel transition.
  }
}

export function useWorkspaceSidePanelMode() {
  const context = useContext(WorkspaceSidePanelContext);
  if (!context) {
    throw new Error(
      "useWorkspaceSidePanelMode must be used within a WorkspaceSidePanelModeProvider",
    );
  }
  return context;
}

export function WorkspaceSidePanelModeProvider({
  children,
  workspaceId,
  isAiAvailable = true,
}: {
  children: ReactNode;
  workspaceId: string;
  isAiAvailable?: boolean;
}) {
  const pathname = usePathname();
  const [state, setState] = useState<WorkspaceSidePanelState>({
    kind: "closed",
  });

  useEffect(() => {
    // Reset persisted panel state whenever the workspace boundary changes.
    void workspaceId;
    if (!isAiAvailable) writeStoredAiPanelOpenState(false);
    setState(
      isAiAvailable && readStoredAiPanelOpenState()
        ? { kind: "ai-chat" }
        : { kind: "closed" },
    );
  }, [isAiAvailable, workspaceId]);

  const openAiChat = useCallback(() => {
    if (!isAiAvailable) return;
    setState({ kind: "ai-chat" });
    writeStoredAiPanelOpenState(true);
  }, [isAiAvailable]);

  const openUserMemo = useCallback((intent: MemoPanelIntent) => {
    setState({ kind: "memo", intent });
  }, []);

  const closeWorkspaceSidePanel = useCallback(() => {
    setState((current) => {
      if (current.kind === "ai-chat") writeStoredAiPanelOpenState(false);
      return { kind: "closed" };
    });
  }, []);

  const toggleAiChat = useCallback(() => {
    if (!isAiAvailable) return;
    setState((current) => {
      const nextOpen = current.kind !== "ai-chat";
      writeStoredAiPanelOpenState(nextOpen);
      return nextOpen ? { kind: "ai-chat" } : { kind: "closed" };
    });
  }, [isAiAvailable]);

  const isOpen = state.kind !== "closed";
  const isVisible = isOpen && !isAiPagePathname(pathname);
  const value = useMemo<WorkspaceSidePanelContextType>(
    () => ({
      state,
      mode: state.kind === "memo" ? "MEMO" : "AI_CHAT",
      userMemoIntent: state.kind === "memo" ? state.intent : null,
      isOpen,
      isVisible,
      isAiAvailable,
      openAiChat,
      openUserMemo,
      closeWorkspaceSidePanel,
      toggleAiChat,
    }),
    [
      state,
      isOpen,
      isVisible,
      isAiAvailable,
      openAiChat,
      openUserMemo,
      closeWorkspaceSidePanel,
      toggleAiChat,
    ],
  );

  return (
    <WorkspaceSidePanelContext.Provider value={value}>
      {children}
    </WorkspaceSidePanelContext.Provider>
  );
}
